import styled, { css } from 'styled-components'
import { colors, fontScope, radius } from '../tokens'
import { ColumnAlign } from './columns'

const JUSTIFY: Record<ColumnAlign, string> = { left: 'flex-start', center: 'center', right: 'flex-end' }

export const Scroll = styled.div`
  ${fontScope}
  min-width: 0;
`

const STICKY_BACKGROUND = {
  header: colors.zebra,
  row: colors.white,
  zebra: colors.zebra,
  subtotal: colors.subtotal,
}

export type StickyTone = keyof typeof STICKY_BACKGROUND

export const Content = styled.div<{ $minWidth: number }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: ${({ $minWidth }) => $minWidth}px;
  padding-right: 8px;

  @media print {
    min-width: 0;
    padding-right: 0;
    gap: 8px;

    & * {
      font-size: 9px !important;
      line-height: 1.3 !important;
      white-space: normal !important;
    }
  }
`

const gridRow = css<{ $template: string, $printTemplate: string }>`
  display: grid;
  grid-template-columns: ${({ $template }) => $template};
  align-items: stretch;

  @media print {
    grid-template-columns: ${({ $printTemplate }) => $printTemplate};
    height: auto;
    min-height: 24px;
    break-inside: avoid;
  }
`

export const HeaderBox = styled.div<{ $template: string, $printTemplate: string }>`
  ${gridRow}
  position: sticky;
  top: 0;
  z-index: 1;
  box-sizing: border-box;
  height: 62px;
  border: 1px solid ${colors.tableBorder};
  border-radius: ${radius.table};
  background: ${colors.zebra};
  overflow: hidden;
  overflow: clip;

  @media print {
    position: static;
  }
`

export const Cell = styled.div<{ $align?: ColumnAlign, $padding?: string, $overflow?: 'hidden' | 'visible', $sticky?: StickyTone }>`
  display: flex;
  align-items: center;
  justify-content: ${({ $align }) => JUSTIFY[$align ?? 'left']};
  gap: 8px;
  min-width: 0;
  padding: ${({ $padding }) => $padding ?? '10px 12px'};
  overflow: ${({ $overflow }) => $overflow ?? 'hidden'};
  text-align: ${({ $align }) => $align ?? 'left'};
  ${({ $sticky }) =>
    $sticky !== undefined &&
    css`
      position: sticky;
      left: 0;
      z-index: 1;
      background: ${STICKY_BACKGROUND[$sticky]};
      box-shadow: inset -1px 0 0 ${colors.tableBorder};
      ${$sticky === 'header' && `border-radius: ${radius.table} 0 0 ${radius.table};`}
    `}

  @media print {
    padding: 3px 4px;
    gap: 4px;
    overflow: visible;
    position: static;
    box-shadow: none;
  }
`

export const HeaderText = styled.span`
  font-size: 12px;
  font-weight: 800;
  line-height: 16.5px;
  letter-spacing: 0.66px;
  text-transform: uppercase;
  color: ${colors.ink500};
`

export const GroupCard = styled.div`
  border: 1px solid ${colors.tableBorder};
  border-radius: ${radius.table};
  background: ${colors.white};
  overflow: hidden;
  overflow: clip;
`

export const GroupHeader = styled.div`
  padding: 16px;
  border-left: 8px solid ${colors.primary};
  background: ${colors.groupHeader};
`

export const GroupHeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 37px;
`

export const GroupName = styled.span`
  font-size: 14px;
  font-weight: 700;
  line-height: 21px;
  color: ${colors.ink950};
  white-space: nowrap;
`

export const Pill = styled.span`
  padding: 2px 10px;
  border-radius: ${radius.pill};
  background: ${colors.white};
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  color: ${colors.ink600};
  white-space: nowrap;
`

export const GroupStock = styled.span`
  font-size: 13px;
  font-weight: 500;
  line-height: 19.5px;
  color: ${colors.ink600};
  white-space: nowrap;
`

export const ProgressWrap = styled.div<{ $width?: string }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: ${({ $width }) => $width ?? '423px'};
  min-width: 160px;
  max-width: 100%;
`

export const ProgressLabel = styled.span`
  flex: 1 0 0;
  min-width: 0;
  font-size: 12px;
  font-weight: 700;
  line-height: 18px;
  color: ${colors.positive};
  white-space: nowrap;
`

export const Row = styled.div<{ $template: string, $printTemplate: string, $striped: boolean }>`
  ${gridRow}
  min-height: 60px;
  border-top: 1px solid ${colors.rowDivider};
  background: ${({ $striped }) => ($striped ? colors.zebra : 'transparent')};
`

export const TicketText = styled.span`
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow-wrap: anywhere;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: ${colors.ink800};

  @media print {
    display: block;
    overflow: visible;
    -webkit-line-clamp: unset;
  }
`

export const LotText = styled.span`
  font-size: 14px;
  font-weight: 500;
  line-height: 21px;
  color: ${colors.ink700};
  white-space: nowrap;
`

export const MutedText = styled.span`
  font-size: 14px;
  font-weight: 400;
  line-height: 21px;
  color: ${colors.ink500};
`

export const QuantityText = styled.span`
  font-size: 14px;
  font-weight: 600;
  line-height: 21px;
  color: ${colors.ink950};
`

export const ValueText = styled.span`
  font-size: 14px;
  font-weight: 500;
  line-height: 21px;
  color: ${colors.ink700};
  white-space: nowrap;
`

export const PaymentText = styled.span`
  font-size: 14px;
  font-weight: 400;
  line-height: 21px;
  color: ${colors.ink600};
`

export const TotalText = styled.span`
  font-size: 14px;
  font-weight: 800;
  line-height: 21px;
  color: ${colors.positive};
  white-space: nowrap;
`

export const SubtotalRow = styled.div<{ $template: string, $printTemplate: string }>`
  ${gridRow}
  height: 60px;
  background: ${colors.subtotal};
`

export const SubtotalLabel = styled.span`
  font-size: 14px;
  font-weight: 700;
  line-height: 21px;
  color: ${colors.ink800};
`

export const StrongText = styled.span`
  font-size: 14px;
  font-weight: 600;
  line-height: 21px;
  color: ${colors.ink950};
  white-space: nowrap;
`

export const SubtotalTotal = styled.span`
  font-size: 14px;
  font-weight: 800;
  line-height: 21px;
  color: ${colors.ink950};
  white-space: nowrap;
`

export const TotalBar = styled.div<{ $template: string, $printTemplate: string }>`
  ${fontScope}
  ${gridRow}
  height: 60px;
  border-radius: ${radius.table};
  background: ${colors.totalBar};
  overflow: hidden;
`

export const TotalLabel = styled.span`
  font-size: 16px;
  font-weight: 700;
  line-height: 21px;
  color: ${colors.white};
  white-space: nowrap;
`

export const TotalQuantity = styled.span`
  font-size: 14px;
  font-weight: 600;
  line-height: 21px;
  color: ${colors.white};
  white-space: nowrap;
`

export const TotalValue = styled.span`
  font-size: 16px;
  font-weight: 800;
  line-height: 21px;
  color: ${colors.positiveOnDark};
  white-space: nowrap;
`
